import * as Sentry from '@sentry/react';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router';
import { QueryClientProvider } from '@tanstack/react-query';
import { App } from '@/app/App';
import { TooltipProvider } from '@/components/ui/tooltip';
import { I18nProvider } from '@/i18n/I18nProvider';
import { ThemeProvider } from '@/theme/ThemeProvider';
import { queryClient } from '@/lib/queryClient';
import { registerMaplibreWorker } from '@/lib/registerMaplibreWorker';
import { registerPmtilesProtocol } from '@/lib/registerPmtilesProtocol';
import { markClassicScrollbars } from '@/lib/classicScrollbars';
import { hasPiiInUrl, scrubPiiDeep, shouldSampleReplayForEvent } from '@/lib/sentryPrivacy';
import '@/index.css';
import 'maplibre-gl/dist/maplibre-gl.css';

const sentryDsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
/** Иначе SDK ругается «Discarded session… missing release» и хуже группировка в Sentry. В CI задайте VITE_SENTRY_RELEASE (например git tag + sha). */
const sentryRelease =
  (import.meta.env.VITE_SENTRY_RELEASE as string | undefined)?.trim() ||
  `sloy-web@${import.meta.env.MODE}`;

if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    release: sentryRelease,
    dist: (import.meta.env.VITE_SENTRY_DIST as string | undefined)?.trim() || undefined,
    environment: (import.meta.env.VITE_SENTRY_ENVIRONMENT as string | undefined) ?? import.meta.env.MODE,
    debug: import.meta.env.DEV,
    // Без IP, cookies, тел запросов и заголовков, кроме User-Agent и Referer (по ним Sentry
    // определяет браузер и ОС) — как `sendDefaultPii: false` в SDK 10. Категории перечислять
    // все: незаданная в SDK 11 собирается.
    dataCollection: {
      userInfo: false,
      cookies: false,
      httpHeaders: { request: { allow: ['user-agent', 'referer'] }, response: false },
      httpBodies: [],
      urlQueryParams: true,
      graphQL: { document: false, variables: false },
      genAI: { inputs: false, outputs: false },
      databaseQueryData: false,
      queues: false,
    },
    // SDK 11 по умолчанию цепляет стек и к captureMessage — сессия считается «errored» из-за
    // перф-сигналов уровня warning. Оставляем поведение SDK 10.
    attachStacktrace: false,
    // Трассировка: 10% запросов в prod; 100% в dev для отладки
    tracesSampleRate: import.meta.env.DEV ? 1.0 : 0.1,
    // Реплей только при ошибках — ловит OOM-краши
    replaysOnErrorSampleRate: 1.0,
    replaysSessionSampleRate: 0,
    // Почты и токены из строк событий, спанов и адресов — см. lib/sentryPrivacy.ts. Транзакций
    // в SDK 11 нет (спаны уходят потоком): бывшая транзакция — корневой спан, он тоже идёт
    // через beforeSendSpan, а beforeSendTransaction не вызывается.
    beforeSend: (event) => scrubPiiDeep(event),
    beforeSendSpan: (span) => scrubPiiDeep(span),
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.httpClientIntegration(),
      // Открыто по ссылке из письма (почта, токен в адресе): адрес страницы в записи не скрыть
      ...(hasPiiInUrl(window.location.href)
        ? []
        : [
            Sentry.replayIntegration({
              // Маскировка SDK по умолчанию, заданная явно: в интерфейсе почты плательщиков,
              // последние цифры карт, имена участников. Читаемым оставлять только статичный
              // текст без данных пользователя — точечно, через `unmask` (docs/sentry-privacy.md).
              maskAllText: true,
              maskAllInputs: true,
              blockAllMedia: true,
              maskAttributes: ['title', 'placeholder', 'aria-label', 'alt'],
              beforeErrorSampling: shouldSampleReplayForEvent,
              beforeAddRecordingEvent: scrubPiiDeep,
            }),
          ]),
    ],
  });
  // `urls` и `request` служебного события реплея не проходят через beforeSend
  Sentry.addEventProcessor((event) => (event.type === 'replay_event' ? scrubPiiDeep(event) : event));

  // Dev: консоль не видит ESM-импорты — экспорт для ручных проверок (captureException и т.д.)
  if (import.meta.env.DEV) {
    (window as Window & { Sentry?: typeof Sentry }).Sentry = Sentry;
  }
}

registerMaplibreWorker();
registerPmtilesProtocol();
markClassicScrollbars();

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <I18nProvider>
        <ThemeProvider>
          <BrowserRouter>
            <TooltipProvider>
              <App />
            </TooltipProvider>
          </BrowserRouter>
        </ThemeProvider>
      </I18nProvider>
    </QueryClientProvider>
  </React.StrictMode>
);
